// src/pages/VacationOverview.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";

const months = [
  "Januari","Februari","Mars","April","Maj","Juni",
  "Juli","Augusti","September","Oktober","November","December"
];
const dayNames = ["sön", "mån", "tis", "ons", "tors", "fre", "lör"];

// ISO Helpers
function getISOWeek(date) {
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = tmp.getUTCDay() || 7; // söndag=7
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
}

function getISOWeekRange(year, week) {
  const simple = new Date(Date.UTC(year, 0, 4 + (week - 1) * 7));
  const day = simple.getUTCDay() || 7;
  const weekStart = new Date(simple);
  weekStart.setUTCDate(simple.getUTCDate() - day + 1);
  const weekEnd = new Date(weekStart);
  weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
  // tillbaka till lokal tid
  return [new Date(weekStart), new Date(weekEnd)];
}

function fmtDM(d) {
  return d.toLocaleDateString("sv-SE", { day: "2-digit", month: "2-digit" });
}
function fmtDMY(d) {
  return d.toLocaleDateString("sv-SE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Bygger etikett för dagar i en vecka
function labelForWeekSpan(v, weekStart, weekEnd) {
  const s = new Date(v.startDate);
  const e = new Date(v.endDate);
  const clipStart = s < weekStart ? new Date(weekStart) : new Date(s);
  const clipEnd = e > weekEnd ? new Date(weekEnd) : new Date(e);

  const covered = [];
  for (let d = new Date(clipStart); d <= clipEnd; d.setDate(d.getDate() + 1)) {
    covered.push(d.getDay());
  }
  const names = covered.map(n => dayNames[n]);
  const uniq = [...new Set(names)];
  if (uniq.length === 7) return "(hela veckan)";
  return `(${uniq.join(", ")})`;
}

export default function VacationOverview() {
  const { user, fetchAllVacations, deleteVacation } = useAuth();
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);

  // klick-öppning för datum-detaljer
  const [openDetailsId, setOpenDetailsId] = useState(null);

  const [currentMonth, setCurrentMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const load = async () => {
    setLoading(true);
    const v = await fetchAllVacations();
    const today = new Date();
    setVacations(
      (v || []).filter(x => x.status === "Beviljad" && new Date(x.endDate) >= today)
    );
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // veckor i månaden
  const weeksInMonth = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const set = new Set();
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      set.add(getISOWeek(new Date(d)));
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [year, month]);

  // gruppera semestrar per vecka (överlappar)
  const weeksData = useMemo(() => {
    const map = new Map();
    for (const w of weeksInMonth) {
      const [wStart, wEnd] = getISOWeekRange(year, w);
      map.set(w, { weekStart: wStart, weekEnd: wEnd, items: [] });
    }
    for (const v of vacations) {
      const s = new Date(v.startDate);
      const e = new Date(v.endDate);
      for (const w of weeksInMonth) {
        const entry = map.get(w);
        if (!entry) continue;
        const { weekStart, weekEnd } = entry;
        if (e < weekStart || s > weekEnd) continue;
        entry.items.push(v);
      }
    }
    return map;
  }, [vacations, weeksInMonth, year, month]);

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const handleDelete = async (idLike) => {
    const id = idLike?.id ?? idLike?._id ?? idLike; // robust
    if (!id) return alert("Saknar id för semestern.");
    if (!window.confirm("Vill du ta bort semestern?")) return;
    await deleteVacation(id);   // <- skickar alltid ett korrekt id
    load();
  };

  const toggleDetails = (id) => {
    setOpenDetailsId((cur) => (cur === id ? null : id));
  };

  return (
    <div className="container mt-4">
      <h3 className="mb-1">Semesteröversikt</h3>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={prevMonth}>‹</button>
        <h4 className="mb-0">{months[month]} {year}</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={nextMonth}>›</button>
      </div>

      {loading ? (
        <div>Laddar semestrar…</div>
      ) : (
        <div className="card">
          <ul className="list-group list-group-flush">
            {[...weeksData.entries()].map(([week, info]) => {
              const { weekStart, weekEnd, items } = info;
              return (
                <li key={week} className="list-group-item">
                  <div className="d-flex align-items-center mb-2">
                    <span className="fw-bold me-2">v{week}</span>
                    <span className="text-muted small">
                      {fmtDM(weekStart)} – {fmtDM(weekEnd)}
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {items.length > 0 ? (
                      items.map((v) => {
                        const titleTip = `${fmtDMY(new Date(v.startDate))} – ${fmtDMY(new Date(v.endDate))}`;
                        const isOpen = openDetailsId === v._id;

                        return (
                          <div
                            key={v._id}
                            className="chip bg-light border rounded-3 px-2 py-1 d-inline-flex align-items-center flex-wrap me-2 mb-2"
                            style={{ cursor: "pointer", maxWidth: "100%" }}
                            title={titleTip}
                            onClick={() => toggleDetails(v._id)}
                          >
                            <Avatar
                              initials={v.userId?.avatar?.initials}
                              color={v.userId?.avatar?.color}
                              size={18}
                            />
                            <span className="ms-1 small fw-semibold text-truncate">
                              {v.userId?.firstName} {v.userId?.lastName}
                            </span>
                            <span className="ms-1 text-muted small">
                              {labelForWeekSpan(v, weekStart, weekEnd)}
                            </span>

                            {/* force new row for dates */}
                            {isOpen && (
                              <span className="chip-break chip-date">
                                {fmtDMY(new Date(v.startDate))} – {fmtDMY(new Date(v.endDate))}
                              </span>
                            )}

                            {user?.role === "admin" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger ms-2 py-0 px-1"
                                onClick={(e) => { e.stopPropagation(); handleDelete(v._id); }}
                                title="Ta bort"
                              >
                                🗑
                              </button>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <span className="text-muted small">Ingen semester denna vecka</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Mobil + chip-polish */}
      <style>{`
        /* Bas: chip (badge) */
        .chip {
          gap: .5rem;
          border: 1px solid #eef1f4;
          border-radius: .5rem;
          background: #f8f9fa;
          max-width: 100%;
        }
        .chip .text-truncate { max-width: 160px; }

        /* Datumrad – läggs på egen rad i mobil */
        .chip-break {
          flex-basis: 100%;
          margin-top: .25rem;
        }
        .chip-date {
          color: #6c757d;
          font-size: .9rem;
        }

        /* Generell badge-styling i listor */
        .list-group-item .badge { font-size: .9rem; }
        .list-group-item .badge .small { font-size: .75rem; }

        /* Mobilanpassning */
        @media (max-width: 576px) {
          .chip { align-items: flex-start; } /* så datum radbryts snyggt */
          .chip .text-truncate { max-width: 120px; }
          .list-group-item .badge { font-size: .85rem; }
          .list-group-item .badge .small { font-size: .7rem; }
          .chip-action { margin-top: .25rem; } /* knappen hamnar fint under på mobil */
        }

        /* Desktop (>= md): datum inline, centrerad radhöjd, knapp i slutet */
        @media (min-width: 768px) {
          .chip { align-items: center; }
          .chip-break {
            flex-basis: auto;      /* inte full rad */
            margin-top: 0;
            margin-left: .5rem;
          }
          .chip-date::before {
            content: "•";
            margin: 0 .5rem;
            color: #ced4da;        /* liten prick-separator före datum */
          }
          .chip-action { margin-left: .5rem; }
        }
      `}</style>


    </div>
  );
}
